import LookupTable from './lookupTable.js';
import CPU_COLORMAPS from '../../../../constants/cpuColormaps.js';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction.js';
import { resolveColormap as resolveSharedColormap } from '../../../../utilities/colormap.js';
const COLOR_TRANSPARENT = [0, 0, 0, 0];
const CPU_FALLBACK_COLOR_COUNT = 256;
const CPU_FALLBACK_GRAYSCALE_NAMES = new Set([
    'gray',
    'grey',
    'grayscale',
    'greyscale',
]);
function getCPUFallbackUnitValue(index) {
    return index / CPU_FALLBACK_COLOR_COUNT;
}
function mapCPUFallbackColorIndex(index, colorCount) {
    return Math.min(colorCount - 1, Math.floor(getCPUFallbackUnitValue(index) * colorCount));
}
function linspace(a, b, n) {
    n = n === null ? 100 : n;
    const increment = (b - a) / (n - 1);
    const vector = [];
    while (n-- > 0) {
        vector.push(a);
        a += increment;
    }
    vector[vector.length - 1] = b;
    return vector;
}
function getRank(array, elem) {
    let left = 0;
    let right = array.length - 1;
    while (left <= right) {
        const mid = left + Math.floor((right - left) / 2);
        const midElem = array[mid];
        if (midElem === elem) {
            return mid;
        }
        else if (elem < midElem) {
            right = mid - 1;
        }
        else {
            left = mid + 1;
        }
    }
    return left;
}
function searchSorted(inputArray, values) {
    let i;
    const indexes = [];
    const len = values.length;
    inputArray.sort(function (a, b) {
        return a - b;
    });
    for (i = 0; i < len; i++) {
        indexes[i] = getRank(inputArray, values[i]);
    }
    return indexes;
}
function makeMappingArray(N, data, gamma) {
    let i;
    const x = [];
    const y0 = [];
    const y1 = [];
    const lut = [];
    gamma = gamma === null ? 1 : gamma;
    for (i = 0; i < data.length; i++) {
        const element = data[i];
        x.push((N - 1) * element[0]);
        y0.push(element[1]);
        y1.push(element[1]);
    }
    const xLinSpace = linspace(0, 1, N);
    for (i = 0; i < N; i++) {
        xLinSpace[i] = (N - 1) * Math.pow(xLinSpace[i], gamma);
    }
    const xLinSpaceIndexes = searchSorted(x, xLinSpace);
    for (i = 1; i < N - 1; i++) {
        const index = xLinSpaceIndexes[i];
        const colorPercent = (xLinSpace[i] - x[index - 1]) / (x[index] - x[index - 1]);
        const colorDelta = y0[index] - y1[index - 1];
        lut[i] = colorPercent * colorDelta + y1[index - 1];
    }
    lut[0] = y1[0];
    lut[N - 1] = y0[data.length - 1];
    return lut;
}
function createLinearSegmentedColormap(segmentedData, N, gamma) {
    let i;
    const lut = [];
    N = N === null ? 256 : N;
    gamma = gamma === null ? 1 : gamma;
    const redLut = makeMappingArray(N, segmentedData.red, gamma);
    const greenLut = makeMappingArray(N, segmentedData.green, gamma);
    const blueLut = makeMappingArray(N, segmentedData.blue, gamma);
    for (i = 0; i < N; i++) {
        const red = Math.round(redLut[i] * 255);
        const green = Math.round(greenLut[i] * 255);
        const blue = Math.round(blueLut[i] * 255);
        const rgba = [red, green, blue, 255];
        lut.push(rgba);
    }
    return lut;
}
export function getColormapsList() {
    const colormaps = [];
    const keys = Object.keys(CPU_COLORMAPS);
    keys.forEach(function (key) {
        if (Object.prototype.hasOwnProperty.call(CPU_COLORMAPS, key)) {
            const colormap = CPU_COLORMAPS[key];
            colormaps.push({
                id: key,
                name: colormap.name,
            });
        }
    });
    colormaps.sort(function (a, b) {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        if (aName === bName) {
            return 0;
        }
        return aName < bName ? -1 : 1;
    });
    return colormaps;
}
export function resolveCPUFallbackColormap(colormap, fallbackColormap, options) {
    const name = typeof colormap === 'string' ? colormap.trim() : colormap?.name?.trim();
    if (!name) {
        return fallbackColormap;
    }
    const normalizedName = name.toLowerCase();
    if (CPU_FALLBACK_GRAYSCALE_NAMES.has(normalizedName)) {
        return undefined;
    }
    const registeredColormap = resolveSharedColormap(name);
    if (registeredColormap) {
        return resolveCPUFallbackOpacityMappedColormap(getOrCreateCPUFallbackColormap(registeredColormap, name), typeof colormap === 'string' ? undefined : colormap, options?.voiRange);
    }
    const colormapId = findCPUFallbackColormapId(name);
    const resolvedBaseColormap = colormapId ? getColormap(colormapId) : undefined;
    return resolveCPUFallbackOpacityMappedColormap(resolvedBaseColormap, typeof colormap === 'string' ? undefined : colormap, options?.voiRange);
}
export function getColormap(id, colormapData) {
    let colormap = CPU_COLORMAPS[id];
    if (!colormap) {
        colormap = CPU_COLORMAPS[id] = colormapData || {
            name: '',
            colors: [],
        };
    }
    if (!colormap.colors && colormap.segmentedData) {
        colormap.colors = createLinearSegmentedColormap(colormap.segmentedData, colormap.numColors, colormap.gamma);
    }
    const cpuFallbackColormap = {
        getId() {
            return id;
        },
        getColorSchemeName() {
            return colormap.name;
        },
        setColorSchemeName(name) {
            colormap.name = name;
        },
        getNumberOfColors() {
            return colormap.colors.length;
        },
        setNumberOfColors(numColors) {
            while (colormap.colors.length < numColors) {
                colormap.colors.push(COLOR_TRANSPARENT);
            }
            colormap.colors.length = numColors;
        },
        getColor(index) {
            if (this.isValidIndex(index)) {
                return colormap.colors[index];
            }
            return COLOR_TRANSPARENT;
        },
        getColorRepeating(index) {
            const numColors = colormap.colors.length;
            index = numColors ? index % numColors : 0;
            return this.getColor(index);
        },
        setColor(index, rgba) {
            if (this.isValidIndex(index)) {
                colormap.colors[index] = rgba;
            }
        },
        addColor(rgba) {
            colormap.colors.push(rgba);
        },
        insertColor(index, rgba) {
            if (this.isValidIndex(index)) {
                colormap.colors.splice(index, 1, rgba);
            }
        },
        removeColor(index) {
            if (this.isValidIndex(index)) {
                colormap.colors.splice(index, 1);
            }
        },
        clearColors() {
            colormap.colors = [];
        },
        buildLookupTable(lut) {
            if (!lut) {
                return;
            }
            const numColors = colormap.colors.length;
            lut.setNumberOfTableValues(numColors);
            for (let i = 0; i < numColors; i++) {
                lut.setTableValue(i, colormap.colors[i]);
            }
        },
        createLookupTable() {
            const lut = new LookupTable();
            this.buildLookupTable(lut);
            return lut;
        },
        isValidIndex(index) {
            return index >= 0 && index < colormap.colors.length;
        },
    };
    return cpuFallbackColormap;
}
function getOrCreateCPUFallbackColormap(colormap, originalName) {
    const id = `vtk:${(colormap.name || colormap.Name || originalName)
        .trim()
        .toLowerCase()}`;
    return getColormap(id, createCPUFallbackColormapData(colormap, originalName));
}
function createCPUFallbackColormapData(colormap, originalName) {
    const transferFunction = vtkColorTransferFunction.newInstance();
    transferFunction.applyColorMap(colormap);
    const [lower, upper] = transferFunction.getRange();
    const colors = Array.from({ length: CPU_FALLBACK_COLOR_COUNT }, (_unused, index) => {
        const t = getCPUFallbackUnitValue(index);
        const x = lower + t * (upper - lower);
        const rgb = [];
        transferFunction.getColor(x, rgb);
        return [
            Math.round(rgb[0] * 255),
            Math.round(rgb[1] * 255),
            Math.round(rgb[2] * 255),
            255,
        ];
    });
    return {
        colors,
        name: colormap.Name || originalName,
        numColors: colors.length,
    };
}
function findCPUFallbackColormapId(name) {
    const normalizedName = name.trim().toLowerCase();
    return Object.keys(CPU_COLORMAPS).find((id) => {
        const colormap = CPU_COLORMAPS[id];
        return (id.toLowerCase() === normalizedName ||
            colormap.name?.trim().toLowerCase() === normalizedName);
    });
}
function resolveCPUFallbackOpacityMappedColormap(baseColormap, colormap, voiRange) {
    if (!baseColormap) {
        return;
    }
    if (colormap?.opacity === undefined && colormap?.threshold === undefined) {
        return baseColormap;
    }
    if (!voiRange || voiRange.upper <= voiRange.lower) {
        return baseColormap;
    }
    const alphaMappedColors = createOpacityMappedColors(baseColormap, colormap, voiRange);
    const id = [
        baseColormap.getId(),
        `lower:${roundColormapNumber(voiRange.lower)}`,
        `upper:${roundColormapNumber(voiRange.upper)}`,
        `opacity:${serializeColormapOpacity(colormap?.opacity)}`,
        `threshold:${roundColormapNumber(colormap?.threshold)}`,
    ].join(':');
    return getColormap(id, {
        colors: alphaMappedColors,
        name: baseColormap.getColorSchemeName(),
        numColors: alphaMappedColors.length,
    });
}
function createOpacityMappedColors(baseColormap, colormap, voiRange) {
    const numberOfColors = Math.max(baseColormap.getNumberOfColors(), 1);
    return Array.from({ length: CPU_FALLBACK_COLOR_COUNT }, (_unused, index) => {
        const sourceIndex = numberOfColors === 1
            ? 0
            : mapCPUFallbackColorIndex(index, numberOfColors);
        const rgba = baseColormap.getColor(sourceIndex);
        const scalarValue = voiRange.lower +
            (voiRange.upper - voiRange.lower) * getCPUFallbackUnitValue(index);
        const opacity = resolveOpacityAtValue(scalarValue, colormap?.opacity, colormap?.threshold);
        return [
            rgba[0],
            rgba[1],
            rgba[2],
            Math.round((rgba[3] / 255) * opacity * 255),
        ];
    });
}
function resolveOpacityAtValue(scalarValue, opacity, threshold) {
    if (threshold !== undefined && scalarValue < threshold) {
        return 0;
    }
    if (opacity === undefined) {
        return 1;
    }
    if (typeof opacity === 'number') {
        return clampToUnit(opacity);
    }
    if (!opacity.length) {
        return 1;
    }
    if (scalarValue <= opacity[0].value) {
        return clampToUnit(opacity[0].opacity);
    }
    for (let i = 1; i < opacity.length; i++) {
        const previous = opacity[i - 1];
        const current = opacity[i];
        if (scalarValue <= current.value) {
            const range = current.value - previous.value;
            const t = range > 0 ? (scalarValue - previous.value) / range : 0;
            return clampToUnit(previous.opacity + (current.opacity - previous.opacity) * t);
        }
    }
    return clampToUnit(opacity[opacity.length - 1].opacity);
}
function clampToUnit(value) {
    return Math.max(0, Math.min(1, value));
}
function serializeColormapOpacity(opacity) {
    if (opacity === undefined) {
        return 'none';
    }
    if (typeof opacity === 'number') {
        return String(roundColormapNumber(opacity));
    }
    return opacity
        .map(({ opacity: alpha, value }) => `${roundColormapNumber(value)}-${roundColormapNumber(alpha)}`)
        .join(',');
}
function roundColormapNumber(value) {
    if (value === undefined) {
        return 'none';
    }
    return String(Math.round(value * 1000) / 1000);
}
